import type { ChatMessage, ChatResponse, ChatSentence } from '@/api/types';
import { GESTURE_MOTIONS } from '@/flow/config/gestureMotions';

export const CHAT_END_WORDS = [
  'ばいばい',
  'おわり',
  'おわりにする',
  'さようなら',
  'またね',
  'やめる',
] as const;

const VALID_MOTION_IDS = new Set(GESTURE_MOTIONS.map(({ id }) => id));

export const normalizeChatText = (text: string): string => (
  text
    .normalize('NFKC')
    .replace(/[ァ-ヶ]/g, (character) => String.fromCharCode(character.charCodeAt(0) - 0x60))
    .replace(/[\s。、！？!?.,・「」『』（）()[\]{}ー〜～…]/g, '')
    .toLowerCase()
);

export const containsChatEndWord = (text: string): boolean => {
  const normalized = normalizeChatText(text);
  return CHAT_END_WORDS.some((word) => normalized.includes(normalizeChatText(word)));
};

export const trimChatHistory = (messages: readonly ChatMessage[]): ChatMessage[] => {
  const latest = messages.slice(-20);
  const aligned = latest[0]?.role === 'assistant' ? latest.slice(1) : latest;
  return aligned.map(({ role, text }) => ({ role, text }));
};

const asSentence = (value: unknown): ChatSentence | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const text = typeof record.text === 'string' ? record.text.trim() : '';
  if (!text) return null;
  const motionId = typeof record.motionId === 'string' && VALID_MOTION_IDS.has(record.motionId)
    ? record.motionId
    : undefined;
  return { text, ...(motionId === undefined ? {} : { motionId }) };
};

export const parseChatResponse = (value: unknown): ChatResponse => {
  if (!value || typeof value !== 'object') {
    throw new Error('おしゃべり応答の形式が不正です');
  }
  const record = value as Record<string, unknown>;
  const text = typeof record.text === 'string' ? record.text.trim() : '';
  const sentences = Array.isArray(record.sentences)
    ? record.sentences.map(asSentence).filter((sentence): sentence is ChatSentence => sentence !== null)
    : [];
  if (sentences.length > 0) {
    return {
      sentences,
      text: text || sentences.map((sentence) => sentence.text).join(''),
    };
  }
  if (text) return { sentences: [{ text }], text };
  throw new Error('おしゃべり応答に発話文がありません');
};

export const splitSpeechText = (text: string, limit = 200): string[] => {
  const characters = [...text.trim()];
  if (characters.length === 0) return [];
  const chunks: string[] = [];
  for (let offset = 0; offset < characters.length; offset += limit) {
    chunks.push(characters.slice(offset, offset + limit).join(''));
  }
  return chunks;
};
